// AnchorScroll - corrects the native jump to `#hash` on page load: prepends
// an invisible helper element into the target section so the browser lands
// `data-scroll-offset` pixels higher (below a fixed header). Behavioral port
// of v1 modules/AnchorScroll.js; the HTML contract (data-scroll-offset on
// the target, .anchor-helper/.helper-element classes) is unchanged.

import { resolveScrollOffset, type ScrollOffset } from '../utils';

export interface AnchorScrollConfig {
  offset: ScrollOffset;
}

const defaultConfig: AnchorScrollConfig = {
  offset: 0,
};

export default class AnchorScroll {
  selector: string;

  config: AnchorScrollConfig;

  constructor(
    selector: string = window.location.hash,
    config: Partial<AnchorScrollConfig> = {},
  ) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  /** Remove all helper elements and clean up. */
  destroy = (): void => {
    document.removeEventListener('click', this.destroy, { capture: true });

    document.querySelectorAll('.anchor-helper').forEach(section => {
      section.classList.remove('anchor-helper');
      section.querySelector('.helper-element')?.remove();
    });
  };

  private _init = (): void => {
    if (!this.selector) {
      return;
    }

    const targetId = this.selector.startsWith('#')
      ? this.selector.slice(1)
      : this.selector;
    const anchorSection = document.getElementById(decodeURIComponent(targetId));

    if (!anchorSection) {
      return;
    }

    this._createHelperElement(anchorSection);

    // The helper shifts layout, so drop it on the first interaction.
    document.addEventListener('click', this.destroy, {
      once: true,
      capture: true,
    });
  };

  private _createHelperElement = (anchorSection: HTMLElement): void => {
    const offset = resolveScrollOffset(anchorSection, this.config.offset);
    const helperElement = document.createElement('div');

    helperElement.className = 'helper-element';
    helperElement.style.height = `${offset}px`;
    helperElement.style.marginTop = `${offset * -1}px`;
    helperElement.style.visibility = 'hidden';
    helperElement.style.pointerEvents = 'none';

    anchorSection.classList.add('anchor-helper');
    anchorSection.insertBefore(helperElement, anchorSection.firstChild);
  };
}
